/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonIcon,
  ButtonVariety,
  IconDelete,
  MessageInline,
  MessageVariety,
  ModalAlert,
  toast,
} from '@sonarsource/echoes-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isDefined } from '~shared/helpers/types';
import {
  useDeleteJiraProjectBindingMutation,
  useJiraLinkedIssuesCountQuery,
  useJiraUserActionsQuery,
} from '../../queries';
import { JiraPermission, JiraProjectBinding } from '../../types';

interface JiraUnbindingConfirmationModalProps {
  jiraProjectName: string;
  projectBinding: JiraProjectBinding;
  shouldCheckForBindingPermissions?: boolean;
  sonarProjectName: string;
}

export default function JiraProjectDisconnectButton(
  props: Readonly<JiraUnbindingConfirmationModalProps>,
) {
  const { projectBinding, jiraProjectName, sonarProjectName, shouldCheckForBindingPermissions } =
    props;
  const { formatMessage } = useIntl();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { data: linkedIssuesCount, isLoading: loadingIssuesCount } = useJiraLinkedIssuesCountQuery(
    projectBinding.sonarProjectId,
  );
  const {
    data: canBindProject = !shouldCheckForBindingPermissions,
    isLoading: isLoadingUserActions,
  } = useJiraUserActionsQuery(projectBinding.sonarProjectId, {
    select: (actions) => actions.includes(JiraPermission.BIND_PROJECT),
    enabled: shouldCheckForBindingPermissions === true,
  });
  const { mutate: deleteJiraProjectBinding, isPending: deletingBinding } =
    useDeleteJiraProjectBindingMutation();

  const performUnbinding = () => {
    deleteJiraProjectBinding(projectBinding.sonarProjectId, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        toast.success({
          description: (
            <FormattedMessage
              id="project_settings.jira.binding.unbind_success"
              values={{
                sqProject: <span className="fs-mask">{sonarProjectName}</span>,
              }}
            />
          ),
          duration: 'medium',
        });
      },
    });
  };

  const handleDisconnect = () => {
    if (isDefined(linkedIssuesCount) && linkedIssuesCount.count > 0) {
      setDeleteDialogOpen(true);
    } else {
      performUnbinding();
    }
  };

  return (
    <>
      <ButtonIcon
        Icon={IconDelete}
        ariaLabel={formatMessage(
          { id: 'project_settings.jira_binding.remove_binding' },
          { name: jiraProjectName },
        )}
        isDisabled={loadingIssuesCount || deletingBinding || !canBindProject}
        isLoading={deletingBinding || isLoadingUserActions}
        onClick={handleDisconnect}
      />
      <ModalAlert
        content={
          <MessageInline variety={MessageVariety.Danger}>
            <FormattedMessage id="project_settings.jira_binding.unbind_confirm.alert" />
          </MessageInline>
        }
        description={
          <FormattedMessage
            id="project_settings.jira_binding.unbind_confirm.description"
            values={{ count: linkedIssuesCount?.count ?? 0 }}
          />
        }
        isOpen={deleteDialogOpen}
        onOpenChange={() => {
          setDeleteDialogOpen(false);
        }}
        primaryButton={
          <Button
            isDisabled={deletingBinding}
            isLoading={deletingBinding}
            onClick={performUnbinding}
            variety={ButtonVariety.Danger}
          >
            <FormattedMessage id="project_settings.jira_binding.disconnect" />
          </Button>
        }
        secondaryButtonLabel={formatMessage({ id: 'cancel' })}
        title={formatMessage({ id: 'project_settings.jira_binding.unbind_confirm.title' })}
      />
    </>
  );
}
